import React from "react";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CONTACT } from "@/data/site";
import { SEO } from "@/data/seo";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const Contacto = () => {
  const { t } = useLang();

  const items = [
    {
      testid: "contact-email",
      icon: Mail,
      label: t({ es: "Email", en: "Email" }),
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
    },
    {
      testid: "contact-phone",
      icon: Phone,
      label: t({ es: "Teléfono", en: "Phone" }),
      value: CONTACT.phone,
      href: `tel:${CONTACT.phone.replace(/\s/g, "")}`,
    },
    {
      testid: "contact-address",
      icon: MapPin,
      label: t({ es: "Dirección", en: "Address" }),
      value: `${CONTACT.address}\n${CONTACT.city}`,
      href: null,
    },
  ];

  return (
    <div data-testid="contacto-page" className="pt-[72px]">
      <Seo {...SEO.contacto} />
      <section className="max-w-7xl mx-auto px-6 pt-20 md:pt-28 pb-24 grid lg:grid-cols-12 gap-16">
        {/* Left */}
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">{t({ es: "Contacto", en: "Contact" })}</span>
            <h1 className="mt-6 font-display text-5xl md:text-6xl font-black text-white leading-[0.95]">
              {t({ es: "Hablemos de tu proyecto", en: "Let's talk about your project" })}
            </h1>
            <p className="mt-6 text-white/60 font-body text-lg">
              {t({
                es: "Marketing, comunicación, eventos o producción audiovisual. Escríbenos o llámanos y te atendemos directamente.",
                en: "Marketing, communication, events or film production. Drop us a line or give us a call — you'll talk to us directly.",
              })}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-cta-email"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-white text-ink px-7 py-4 font-display text-sm font-bold uppercase tracking-widest hover:bg-brand-gold transition-colors"
            >
              {t({ es: "Escríbenos un email", en: "Send us an email" })}
              <ArrowUpRight size={18} />
            </a>
          </Reveal>
        </div>

        {/* Contact data */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10 divide-y divide-white/10" data-testid="contact-data">
              {items.map(({ testid, icon: Icon, label, value, href }) => {
                const content = (
                  <div className="flex items-start gap-5 py-6 first:pt-0 last:pb-0">
                    <span className="w-12 h-12 shrink-0 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                      <Icon size={20} className="text-brand-gold" />
                    </span>
                    <div>
                      <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/40">{label}</p>
                      <p className="mt-1.5 text-white text-lg md:text-xl font-body whitespace-pre-line leading-snug">{value}</p>
                    </div>
                  </div>
                );
                return href ? (
                  <a key={testid} href={href} data-testid={testid} className="block group transition-colors [&_p:last-child]:group-hover:text-brand-gold">
                    {content}
                  </a>
                ) : (
                  <div key={testid} data-testid={testid}>{content}</div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
